import { keyframes } from "@mui/material";
import { GridRowClassNameParams } from "@mui/x-data-grid";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";

import { useTranslation } from "@/context/I18nProvider";
import { OrderMove, OrderMoveResult, PaginationState } from "@/types";
import { handleError } from "@/utils";

interface Options<T extends { _id: string }> {
  // The rows the table shows, in order.
  rows: T[];
  move: (id: string, move: OrderMove) => Promise<OrderMoveResult>;
  reload: () => unknown;
  // Field that splits the table into separately ordered lists (e.g. a footer item's `pid`).
  groupKey?: keyof T;
  // Server-paginated tables only.
  paging?: {
    pagination: PaginationState;
    setPagination: Dispatch<SetStateAction<PaginationState>>;
    // Rows of another 0-based page, with the table's filters.
    fetchPage: (page: number) => Promise<T[]>;
    // False while a filter hides rows: a position then does not tell the page.
    unfiltered?: boolean;
  };
}

const flash = keyframes`from { background-color: rgba(25, 118, 210, 0.28); } to { background-color: transparent; }`;

// Pass to the DataGrid `sx` so the moved row flashes where it landed.
export const movedRowSx = { "& .row-moved": { animation: `${flash} 2s ease-out` } };

// Moves rows through the `/:id/move` endpoint and follows the row to the page it lands on.
export const useOrderMove = <T extends { _id: string }>({
  rows,
  move,
  reload,
  paging,
  groupKey,
}: Options<T>) => {
  const { t } = useTranslation();
  const [moving, setMoving] = useState<boolean>(false);
  const [movedId, setMovedId] = useState<string>();

  useEffect(() => {
    if (!movedId) return;
    const timer = setTimeout(() => setMovedId(undefined), 2000);
    return () => clearTimeout(timer);
  }, [movedId]);

  const run = useCallback(
    async (id: string, body: OrderMove, stepPage?: number) => {
      setMoving(true);
      try {
        const { position } = await move(id, body);
        if (!("targetId" in body)) toast.success(t("order.moved", { position }));
        setMovedId(id);
        const current = paging?.pagination.page ?? 0;
        const page =
          stepPage ??
          (paging && paging.unfiltered !== false
            ? Math.floor((position - 1) / paging.pagination.pageSize)
            : current);
        // Changing page reloads through the table's own effect.
        if (paging && page !== current) paging.setPagination((prev) => ({ ...prev, page }));
        else await reload();
      } catch (error) {
        handleError(error);
      } finally {
        setMoving(false);
      }
    },
    [move, reload, paging, t],
  );

  const moveTo = useCallback((row: T, body: OrderMove) => run(row._id, body), [run]);

  // One step: take the place of the row above or below, fetching it across a page edge.
  const moveStep = useCallback(
    async (index: number, direction: "up" | "down") => {
      const current = rows[index];
      if (!current) return;
      let target: T | undefined = rows[direction === "up" ? index - 1 : index + 1];
      let page: number | undefined;
      if (!target && paging) {
        page = paging.pagination.page + (direction === "up" ? -1 : 1);
        if (page < 0) return;
        try {
          const data = await paging.fetchPage(page);
          target = direction === "up" ? data[data.length - 1] : data[0];
        } catch (error) {
          handleError(error);
          return;
        }
      }
      if (!target || (groupKey && String(target[groupKey]) !== String(current[groupKey]))) return;
      await run(current._id, { targetId: target._id }, page);
    },
    [rows, paging, run, groupKey],
  );

  // True when the neighbour on this page belongs to another list, so the step has nowhere to go.
  const atGroupEdge = useCallback(
    (index: number, direction: "up" | "down") => {
      const target = rows[direction === "up" ? index - 1 : index + 1];
      return Boolean(
        groupKey && target && String(target[groupKey]) !== String(rows[index]?.[groupKey]),
      );
    },
    [rows, groupKey],
  );

  const getRowClassName = useCallback(
    (params: GridRowClassNameParams) => (params.id === movedId ? "row-moved" : ""),
    [movedId],
  );

  return { moving, moveTo, moveStep, atGroupEdge, getRowClassName };
};
